import * as React from 'react';
import { QuestionMarkCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import { ChatIcon, DocumentIcon, LightBulbIcon } from '@heroicons/react/outline';
import { Crisp } from 'crisp-sdk-web';
import { useT } from '../../i18n/LanguageProvider';

/**
 * Floating help launcher (bottom-right). Uses plain Tailwind for the menu instead
 * of MUI MenuList — MUI's style injection can occasionally fail to apply, which
 * made the menu render as a horizontal, overlapping row. This keeps it a clean,
 * fixed-width vertical popover anchored above the button in every environment.
 */
const ChatBox = () => {
	const { t } = useT();
	const [open, setOpen] = React.useState<boolean>(false);
	const ref = React.useRef<HTMLDivElement>(null);

	// Close when clicking outside the widget.
	React.useEffect(() => {
		const onClick = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener('mousedown', onClick);
		return () => document.removeEventListener('mousedown', onClick);
	}, []);

	const close = () => {
		setOpen(false);
		Crisp.chat.hide();
	};

	const items = [
		{
			label: t('help.requestFeature'),
			icon: <LightBulbIcon className="w-5 h-5 text-slate-500" />,
			onClick: () => {
				window.open('https://app.loopedin.io/cardclan#/ideas', '_blank', 'noopener,noreferrer');
				setOpen(false);
			},
		},
		{
			label: t('help.helpDocs'),
			icon: <DocumentIcon className="w-5 h-5 text-slate-500" />,
			onClick: () => {
				window.open('https://docs.cardclan.io/introduction/', '_blank', 'noopener,noreferrer');
				setOpen(false);
			},
		},
		{
			label: t('help.contactSupport'),
			icon: <ChatIcon className="w-5 h-5 text-slate-500" />,
			onClick: () => {
				Crisp.chat.show();
				Crisp.chat.open();
				setOpen(false);
			},
		},
	];

	return (
		<div ref={ref} className="fixed right-8 bottom-8 z-[100] flex flex-col items-end">
			{open && (
				<div className="mb-3 w-56 bg-white border border-slate-200 shadow-xl rounded-2xl overflow-hidden">
					{items.map((item, i) => (
						<button
							key={i}
							onClick={item.onClick}
							className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
						>
							{item.icon}
							<span className="whitespace-nowrap">{item.label}</span>
						</button>
					))}
				</div>
			)}
			<button
				type="button"
				aria-label={open ? t('common.close') : t('help.helpMenu')}
				onClick={() => (open ? close() : setOpen(true))}
				className="w-12 h-12 rounded-full bg-blue-700 text-white shadow-lg flex items-center justify-center hover:bg-blue-800 transition-colors"
			>
				{open ? <XCircleIcon className="w-7 h-7" /> : <QuestionMarkCircleIcon className="w-7 h-7" />}
			</button>
		</div>
	);
};

export default ChatBox;
